import { useEffect, useState, useContext } from 'react'
import { Outlet, useLocation, Navigate, Link } from 'react-router-dom'
import { AuthContext } from '../../App'
import { supabase } from '../../supabase'
import RouteSkeleton from '../RouteSkeleton'

const ONBOARDING_TOTAL_STEPS = 2

export default function AppLayout() {
  const { user } = useContext(AuthContext)
  const { pathname } = useLocation()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase
      .from('profiles')
      .select('onboarding_step, plan, trial_ends_at')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        setProfile(data || {})
        setLoading(false)
      })
  }, [user])

  if (loading) return <RouteSkeleton />

  const needsOnboarding = (profile?.onboarding_step ?? 0) < ONBOARDING_TOTAL_STEPS
  const onOnboardingRoute = pathname.startsWith('/app/onboarding')

  if (needsOnboarding && !onOnboardingRoute) {
    return <Navigate to="/app/onboarding/1" replace />
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--sg-bg)' }}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-ink-900 focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <TrialBanner profile={profile} />
      <main id="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}

// ── Trial banner ──────────────────────────────────────────────────────────────

function daysUntil(iso) {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Math.ceil(ms / 86400000)
}

function TrialBanner({ profile }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('sg-trial-banner-dismissed') === '1'
  )

  if (dismissed) return null
  if (!profile?.trial_ends_at) return null
  if (profile.plan === 'free') return null

  const days = daysUntil(profile.trial_ends_at)
  if (days === null || days > 3 || days < 0) return null

  function dismiss() {
    sessionStorage.setItem('sg-trial-banner-dismissed', '1')
    setDismissed(true)
  }

  const message = days === 0
    ? 'Your Premium trial ends today.'
    : days === 1
      ? 'Your Premium trial ends in 1 day.'
      : `Your Premium trial ends in ${days} days.`

  return (
    <div className="bg-brand-600 text-white">
      <div className="mx-auto max-w-container px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3 text-sm">
        <p className="font-medium">
          {message} Add a card to keep going.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/app/billing"
            className="inline-flex items-center justify-center h-8 px-3 rounded-md bg-white text-brand-700 font-semibold text-xs hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Add payment
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss trial banner"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  )
}
